import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { fmtS, pct } from '../../utils/formatters'
import { SkeletonStats, SkeletonCard } from '../ui/Skeleton'

const SHORTCUTS = [
  { icon: '🏗️', label: 'Tambah Proyek', path: '/projects' },
  { icon: '📋', label: 'Laporan Harian', path: '/reports' },
  { icon: '💰', label: 'RAB & AHS', path: '/rab' },
  { icon: '💳', label: 'Invoice', path: '/invoices' },
]

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    activeProjects: 0, totalBudget: 0, totalPaid: 0,
    workers: 0, lowStock: 0, overdue: 0, avgProgress: 0
  })
  const [recentReports, setRecentReports] = useState([])
  const [projects, setProjects] = useState([])

  useEffect(() => {
    if (!user) return
    fetchDashboard()
  }, [user])

  const fetchDashboard = async () => {
    setLoading(true)
    const [{ data: projects }, { data: invoices }, { data: materials }, { data: workers }, { data: reports }] =
      await Promise.all([
        supabase.from('projects').select('*').eq('user_id', user.id),
        supabase.from('invoices').select('*').eq('user_id', user.id),
        supabase.from('materials').select('*').eq('user_id', user.id),
        supabase.from('workers').select('*').eq('user_id', user.id),
        supabase.from('daily_reports').select('*, projects(name)').eq('user_id', user.id)
          .order('created_at', { ascending: false }).limit(5),
      ])

    if (projects) {
      setProjects(projects)
      const active = projects.filter(p => p.status === 'active')
      const totalBudget = projects.reduce((s, p) => s + (p.budget || 0), 0)
      const avgProgress = active.length
        ? Math.round(active.reduce((s, p) => s + (p.progress || 0), 0) / active.length) : 0
      setStats(prev => ({ ...prev, activeProjects: active.length, totalBudget, avgProgress }))
    }
    if (invoices) {
      const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.amount || 0), 0)
      const overdue = invoices.filter(i => i.status === 'overdue').length
      setStats(prev => ({ ...prev, totalPaid, overdue }))
    }
    if (materials) {
      const lowStock = materials.filter(m => m.stock <= m.min_stock).length
      setStats(prev => ({ ...prev, lowStock }))
    }
    if (workers) setStats(prev => ({ ...prev, workers: workers.length }))
    if (reports) setRecentReports(reports)
    setLoading(false)
  }

  if (loading) {
    return (
      <div>
        <SkeletonStats cols={4} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <SkeletonCard lines={4} />
          <SkeletonCard lines={4} />
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Overdue Alert */}
      {stats.overdue > 0 && (
        <div
          style={{
            background: 'var(--danger-bg)', border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-lg)', padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap'
          }}
        >
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: 'var(--danger)', fontSize: 13 }}>
              {stats.overdue} Invoice Jatuh Tempo!
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Segera cek dan proses pembayaran</div>
          </div>
          <button className="btn btn-danger btn-sm" onClick={() => navigate('/invoices')}>Cek →</button>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>🏗️</div>
          <div className="stat-value">{stats.activeProjects}</div>
          <div className="stat-label">Proyek Aktif</div>
          <div className="stat-change" style={{ color: 'var(--success)' }}>Avg: {stats.avgProgress}%</div>
          <div className="progress-bar" style={{ marginTop: 6 }}>
            <div className="fill" style={{ width: `${stats.avgProgress}%`, background: 'var(--success)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--success-bg)', color: 'var(--success)' }}>💰</div>
          <div className="stat-value">{fmtS(stats.totalBudget)}</div>
          <div className="stat-label">Total Nilai Proyek</div>
          <div className="stat-change" style={{ color: 'var(--info)' }}>
            Terbayar: {fmtS(stats.totalPaid)} ({pct(stats.totalPaid, stats.totalBudget)}%)
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>👷</div>
          <div className="stat-value">{stats.workers}</div>
          <div className="stat-label">Total Pekerja</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: stats.lowStock ? 'var(--danger-bg)' : 'var(--success-bg)', color: stats.lowStock ? 'var(--danger)' : 'var(--success)' }}>📦</div>
          <div className="stat-value" style={{ color: stats.lowStock ? 'var(--danger)' : 'var(--success)' }}>
            {stats.lowStock || '✓'}
          </div>
          <div className="stat-label">{stats.lowStock ? 'Stok Menipis' : 'Stok Aman'}</div>
        </div>
      </div>

      {/* Quick Access Shortcuts */}
      <div className="shortcut-grid">
        {SHORTCUTS.map(s => (
          <div key={s.path} className="shortcut-btn" onClick={() => navigate(s.path)}>
            <div className="sb-icon">{s.icon}</div>
            <div className="sb-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="dash-grid">
        {/* Project Progress */}
        <div className="card">
          <div className="card-header">
            <h3>📊 Progress Proyek</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/projects')}>Lihat Semua</button>
          </div>
          <div className="card-body">
            {projects.filter(p => p.status === 'active').length === 0 ? (
              <div className="empty-state"><p>Belum ada proyek aktif</p></div>
            ) : (
              projects.filter(p => p.status === 'active').map(p => (
                <div key={p.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700, marginLeft: 8 }}>
                      {p.progress}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div className="fill" style={{
                      width: `${p.progress}%`,
                      background: p.progress >= 70 ? 'var(--success)' : p.progress >= 40 ? 'var(--accent)' : 'var(--warning)'
                    }} />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                    {p.client} • {fmtS(p.budget)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Reports */}
        <div className="card">
          <div className="card-header">
            <h3>📋 Laporan Terbaru</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/reports')}>Semua</button>
          </div>
          <div className="card-body no-pad">
            {recentReports.length === 0 ? (
              <div className="empty-state"><p>Belum ada laporan</p></div>
            ) : (
              recentReports.map(r => (
                <div key={r.id} className="list-item">
                  <div style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--accent)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.projects?.name || 'Proyek'}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                      {r.activities?.substring(0, 45)}...
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--accent)', flexShrink: 0 }}>
                    {new Date(r.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}